import { IoChevronDownOutline } from "react-icons/io5";
import ReactSelect, { Props as SelectProps } from "react-select";
import { clsxm } from "@/utils/clsxm";

interface ISelectOption<T> {
  value: T;
  label?: string;
}

interface IProps<T>
  extends Omit<SelectProps<ISelectOption<T>, false>, "onChange"> {
  onValueChange?: (value: T) => void;
  label?: string;
  errorMessage?: string | boolean;
}

const Select = <T,>({
  onValueChange,
  label,
  errorMessage,
  ...props
}: IProps<T>) => (
  <div className="w-full flex flex-col min-w-0">
    {label && (
      <label className="mb-1 text-sm font-light text-new-design-gray-500/50 sm:text-base">
        {label}
      </label>
    )}
    <ReactSelect
      menuPortalTarget={document.body}
      isSearchable={false}
      onChange={(option) => {
        if (option && onValueChange) {
          onValueChange(option.value);
        }
      }}
      components={{
        DropdownIndicator: ({ isFocused, isRtl }) => (
          <IoChevronDownOutline
            className={clsxm(
              "flex-0 max-h-[16px] max-w-[16px] text-new-design-gray-500 transition-transform duration-300",
              isFocused && "rotate-180"
            )}
          />
        ),
      }}
      styles={{
        menuPortal: (base) => ({
          ...base,
          zIndex: 9999,
        }),
        control: (base, state) => ({
          ...base,
          borderRadius: "16px",
          padding: "10px 16px 10px 10px",
          boxShadow: "none",
          whiteSpace: "nowrap",
          borderColor: errorMessage
            ? "#CD0707"
            : state.isFocused
            ? "#6160B0"
            : "#1D1D1D14",
          "&:hover": {
            borderColor: "#6160B0",
            cursor: "pointer",
          },
        }),
        singleValue: (base) => ({
          ...base,
          color: "#1F1F1F",
        }),
        menu: (base) => ({
          ...base,
          overflow: "hidden",
          borderRadius: "16px",
          padding: "0px 8px",
          border: "1px solid #6160B0",
        }),
        placeholder: (base) => ({
          ...base,
          color: "#1F1F1F7A",
        }),
        option: (base, props) => ({
          ...base,
          color: "#1A0053",
          borderRadius: "8px",
          margin: "6px 0",
          padding: "6px 12px",
          backgroundColor: props.isSelected ? "#F8F7FE" : "transparent",
          "&:hover": {
            backgroundColor: "#F8F7FE",
          },
        }),
        indicatorSeparator: () => ({
          display: "none",
        }),
      }}
      {...props}
    />
    {errorMessage && (
      <span className="text-left block mt-2 text-sm text-new-design-red-400">
        {errorMessage}
      </span>
    )}
  </div>
);

export { Select };
